/**
 * Читаемость текста на цветной подложке.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ НУЖНО. Цвета статусов оборудования заданы по смыслу, а не по яркости
 * (см. enums/InventoryItemStatusColor): рядом стоят тёмно-красный #e53935 и почти жёлтый
 * #f2c94c. Пока статус был серой подписью в 12 точек, это было незаметно; крупная цветная
 * пилюля с белым текстом на жёлтом не читается вовсе. Поэтому цвет текста не задаётся
 * в стилях константой, а выводится из подложки.
 *
 * Это чистое отображение: на данные, права и бизнес-логику не влияет никак.
 */

/**
 * Порог яркости, ниже которого поверх подложки ставится светлый текст.
 *
 * Не на глаз: 0.3 — точка, в которой для всех шести цветов статусов контраст остаётся
 * не ниже 3:1 (порог WCAG AA для крупного полужирного текста). Выше неё светлый текст
 * на жёлтом и сером проваливается, ниже — тёмный текст на красном и синем.
 */
const LIGHT_TEXT_BELOW = 0.3;

const DARK_TEXT = '#1f2328';
const LIGHT_TEXT = '#ffffff';

/**
 * Каналы цвета из HEX-записи.
 *
 * @param {string} color Вида #rgb или #rrggbb.
 * @returns {number[]|null} [r, g, b] в диапазоне 0…255 или null, если запись не разобрана.
 */
function channelsOf(color) {
    const hex = String(color ?? '').trim();
    const short = /^#([\da-f])([\da-f])([\da-f])$/i.exec(hex);
    const full = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);

    if (short) {
        return [short[1], short[2], short[3]].map((channel) => parseInt(channel + channel, 16));
    }

    if (full) {
        return [full[1], full[2], full[3]].map((channel) => parseInt(channel, 16));
    }

    return null;
}

/**
 * Относительная яркость по WCAG 2.1.
 *
 * @param {number[]} channels
 * @returns {number} 0 — чёрный, 1 — белый.
 */
function relativeLuminance(channels) {
    const [red, green, blue] = channels.map((channel) => {
        const value = channel / 255;

        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

/**
 * Цвет текста, читаемый поверх заданной подложки.
 *
 * Неразобранный цвет считается тёмным: подложка по умолчанию у статусов серая, и белый
 * текст на ней хотя бы виден, тогда как тёмный на неизвестном цвете может исчезнуть.
 *
 * @param {string} background
 * @returns {string}
 */
export function readableTextOn(background) {
    const channels = channelsOf(background);

    if (!channels) {
        return LIGHT_TEXT;
    }

    return relativeLuminance(channels) < LIGHT_TEXT_BELOW ? LIGHT_TEXT : DARK_TEXT;
}

/**
 * Тот же цвет с прозрачностью — для мягкой тени под цветным элементом.
 *
 * @param {string} color
 * @param {number} alpha 0…1
 * @returns {string} rgba(...) или 'transparent', если цвет не разобран.
 */
export function rgbaOf(color, alpha) {
    const channels = channelsOf(color);

    if (!channels) {
        return 'transparent';
    }

    return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${alpha})`;
}
